import { useState } from 'react';
import { propertyAdminService } from 'services/api';
import AvisoModal from 'components/AvisoModal';
import styles from './PropertyAdminEditModal.module.css';

// Límites y formato — mismos que valida el backend (propertyAdminsController.js)
// para no depender solo de ese lado.
const FIELD_MAX_LENGTH = { nombre: 150, apellido: 150 };
const PHONE_REGEX = /^\d{7,10}$/;

// Trim antes de exigir "no vacío" — mismo hallazgo que en Property/Companies:
// un valor de solo espacios pasaba el `required` nativo del input.
function validarCampos(form) {
  if (!form.nombre.trim()) return 'El nombre es requerido';
  for (const campo of ['nombre', 'apellido']) {
    if (form[campo].length > FIELD_MAX_LENGTH[campo]) {
      return `El campo "${campo}" no puede tener más de ${FIELD_MAX_LENGTH[campo]} caracteres`;
    }
  }
  if (form.telefono.trim() && !PHONE_REGEX.test(form.telefono.trim())) {
    return 'El teléfono no es válido — debe tener 7 (fijo) o 10 (celular) dígitos, sin espacios ni +57';
  }
  return null;
}

/**
 * PropertyAdminEditModal — edita nombre/apellido/teléfono de un admin de
 * propiedad ya asignado. A propósito NO permite cambiar la propiedad ni el
 * email: reasignar de propiedad ya tiene su propio camino (pestaña "Asignar
 * admin existente" en PropertyAdminModal, vía POST /assign) — mezclar los
 * dos acá abriría dos caminos para el mismo cambio. El email tampoco se
 * edita porque identifica al usuario para login.
 */
export default function PropertyAdminEditModal({ propertyAdmin, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre:   propertyAdmin?.user?.name      || '',
    apellido: propertyAdmin?.user?.last_name || '',
    telefono: propertyAdmin?.user?.phone     || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const campoError = validarCampos(form);
    if (campoError) return setError(campoError);
    setSaving(true); setError('');
    try {
      await propertyAdminService.update(propertyAdmin.id, form);
      onSaved('Administrador actualizado correctamente');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalEditarAdmin}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3><i className={`fas fa-user-pen ${styles.iconoTitulo}`} />
              Editar administrador
            </h3>
            {propertyAdmin?.property?.name && (
              <div className={styles.propiedad}>
                <i className={`fas fa-building ${styles.iconoPropiedad}`} />{propertyAdmin.property.name}
              </div>
            )}
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={save} noValidate>
          <div className={`modal-body ${styles.cuerpo}`}>
            {error && <AvisoModal>{error}</AvisoModal>}

            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" value={propertyAdmin?.user?.email || ''} disabled />
              <div className={styles.nota}>El email no puede modificarse</div>
            </div>

            <div className={styles.rejilla}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-control" value={form.nombre} onChange={e => set('nombre', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Apellido</label>
                <input className="form-control" value={form.apellido} onChange={e => set('apellido', e.target.value)} />
              </div>
            </div>

            <div className={`form-group ${styles.grupoSinMargen}`}>
              <label className="form-label">Teléfono</label>
              <input className="form-control" value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="3001234567" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> Guardando...</> : <><i className="fas fa-save" /> Guardar</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
